<template>
  <v-skeleton-loader v-if="loading" type="list-item-two-line, list-item-two-line"></v-skeleton-loader>

  <v-form v-model="isValid" v-else ref="form">
    <v-row>
      <v-col cols="12" sm="12" v-if="!isCustomSenderSelection">
        <!-- Single sender control: pick an inbox when several exist, otherwise just display the one
             available inbox (read-only) or the "default" fallback when the partner has no inbox. -->
        <v-select
          v-if="partnerId && hasInboxes"
          dense
          outlined
          :label="$t('components.template.dialog.preview.sender')"
          v-model="inbox"
          :items="inboxes"
          :loading="loadingInboxSenders"
          :disabled="loading || !canSelectInbox"
          :readonly="!canSelectInbox"
          item-text="name"
          item-value="identifier"
          return-object
          @change="selectInboxSender"
          hide-details="auto"
          :clearable="canSelectInbox"
          @click:clear="clearInboxSender"
        >
          <template v-slot:selection="{ item }">
            <v-icon small class="mr-1">{{ item.icon }}</v-icon>
            {{ inboxLabel(item) }}
          </template>
          <template v-slot:item="{ item }">
            <v-icon small class="mr-2">{{ item.icon }}</v-icon>
            {{ inboxLabel(item) }}
          </template>
        </v-select>
        <v-text-field
          v-else
          :label="$t('components.template.dialog.preview.sender')"
          :value="defaultSenderLabel"
          flat
          outlined
          dense
          hide-details="auto"
          disabled
        />
      </v-col>
      <v-col cols="12" sm="12" v-else>
        <slot name="senderSelection" />
      </v-col>
      <v-col cols="12" sm="12">
        <recipient-select
          v-if="isReceiverSearchEnabled && isEmailInbox"
          dense
          :label="$t('components.template.dialog.preview.receiver')"
          :items="recipientOptions"
          :prefill="recipientPrefill"
          :rules="isEmailInbox ? receiverMailRules : phoneNumberRules"
          :recommendedReceivers="recommendedReceivers"
          :partnerId="partnerId"
          @changeSelection="changeReceiverSelection"
          @changeIsValid="changeIsReceiverSelectionValid"
          hide-details="auto"
        >
          <template #appendActions>
            <!-- tabindex="-1": keep these toggles out of the tab order so Tab moves
                 from the recipient field straight to the subject, not the Cc/Bcc buttons. -->
            <v-btn
              v-if="!showCc"
              text
              x-small
              class="mt-1"
              tabindex="-1"
              :title="$t('components.template.dialog.preview.cc')"
              @click="showCc = true"
            >
              {{ $t("components.template.dialog.preview.cc") }}
            </v-btn>
            <v-btn
              v-if="!showBcc"
              text
              x-small
              class="mt-1"
              tabindex="-1"
              :title="$t('components.template.dialog.preview.bcc')"
              @click="showBcc = true"
            >
              {{ $t("components.template.dialog.preview.bcc") }}
            </v-btn>
          </template>
        </recipient-select>
        <!--
          Keyed on the channel: ComboBox reads `prefill` once, in its data initializer, so without a
          remount switching the inbox to SMS would leave the e-mail address sitting under a
          phone-number rule.
        -->
        <combo-box
          v-else
          :key="isEmailInbox ? 'receiver-email' : 'receiver-phone'"
          dense
          :label="$t('components.template.dialog.preview.receiver')"
          :items="recipientOptions"
          :prefill="recipientPrefill"
          :rules="isEmailInbox ? receiverMailRules : phoneNumberRules"
          @changeSelection="changeReceiverSelection"
          @changeIsValid="changeIsReceiverSelectionValid"
          hide-details="auto"
        />

        <recipient-select
          v-if="isReceiverSearchEnabled && isEmailInbox && showCc"
          class="mt-2"
          dense
          :label="$t('components.template.dialog.preview.cc')"
          :items="cc"
          :prefill="cc"
          :rules="receiverMailRules"
          :isRequired="false"
          :recommendedReceivers="recommendedReceivers"
          :partnerId="partnerId"
          @changeSelection="changeCcSelection"
          hide-details="auto"
        />

        <recipient-select
          v-if="isReceiverSearchEnabled && isEmailInbox && showBcc"
          class="mt-2"
          dense
          :label="$t('components.template.dialog.preview.bcc')"
          :items="bcc"
          :prefill="bcc"
          :rules="receiverMailRules"
          :isRequired="false"
          :recommendedReceivers="recommendedReceivers"
          :partnerId="partnerId"
          @changeSelection="changeBccSelection"
          hide-details="auto"
        />
      </v-col>
    </v-row>

    <v-row v-if="isEmailInbox">
      <v-col cols="12">
        <v-text-field
          ref="subjectField"
          dense
          v-model="renderedSubject"
          :rules="textRules"
          outlined
          :label="$t('components.template.dialog.preview.subject')"
          hide-details="auto"
        >
          <template #append>
            <html-editor-placeholder-menu :resolveToken="resolveTokenDisplayValue" @select="insertSubjectPlaceholder" />
          </template>
        </v-text-field>
      </v-col>
    </v-row>

    <v-row dense no-gutters>
      <v-col
        cols="12"
        class="d-flex align-center mt-2"
        v-if="
          partnerId &&
          portalContext &&
          canCreateDocument &&
          isAttachmentEnabled &&
          isEmailInbox &&
          !isHtmlEditorV2Enabled
        "
      >
        <v-menu v-model="attachmentMenu" offset-y left :close-on-content-click="true">
          <template v-slot:activator="{ on, attrs }">
            <v-btn text small class="px-0" v-bind="attrs" v-on="on">
              <v-icon small class="mr-1">mdi-paperclip</v-icon>
              {{ $t("components.template.dialog.preview.addAttachment") }}
            </v-btn>
          </template>

          <v-list dense>
            <v-list-item @click="onPickLocalFile">
              <v-list-item-title>{{ $t("components.template.dialog.preview.browseThisComputer") }}</v-list-item-title>
            </v-list-item>

            <v-list-item @click="documentSelectDialog = true">
              <v-list-item-title>
                {{ $t("components.template.dialog.preview.selectFileFromPlatform") }}
              </v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </v-col>

      <input
        ref="localFileInput"
        type="file"
        multiple
        style="display: none"
        accept="application/pdf,.pdf"
        @change="onLocalFilesSelected"
      />

      <document-selection-simple-dialog
        :isDialogActive.sync="documentSelectDialog"
        :filters="attachmentFileFilters"
        :selectedItems.sync="selected"
        @selected="attachDocuments"
        :key="'document-selection-dialog-' + refKey"
      />

      <v-col cols="12">
        <html-editor
          v-if="isHtmlEditorV2Enabled && !isPlain"
          v-model="renderedBody"
          :partnerId="partnerId"
          :missingTokens="missingTokenPaths"
          :label="$t('components.template.detail.body.body')"
          :key="'html-' + k"
          :resolveToken="resolveTokenValue"
          :getTokenValue="resolveTokenDisplayValue"
          @token-click="onTokenClick"
          @placeholder-missing="onTokenClick"
          @files-dropped="onEditorFilesDropped"
        >
          <template #actions>
            <v-tooltip bottom>
              <template #activator="{ on, attrs }">
                <v-btn
                  v-if="hasTokens"
                  small
                  text
                  tabindex="-1"
                  :color="isTokenWithoutValue ? 'error' : ''"
                  v-bind="attrs"
                  v-on="on"
                  @click="openCustomTokenDialog"
                >
                  <v-icon small :color="isTokenWithoutValue ? 'error' : ''">mdi-list-status</v-icon>
                </v-btn>
              </template>
              <span>{{ $t("components.template.card.editTokensTooltip") }}</span>
            </v-tooltip>

            <v-spacer></v-spacer>
            <v-menu
              v-if="partnerId && portalContext && canCreateDocument && isAttachmentEnabled && isEmailInbox"
              offset-y
              left
              :close-on-content-click="true"
            >
              <template #activator="{ on: menuOn, attrs }">
                <v-tooltip bottom>
                  <template #activator="{ on: tipOn }">
                    <v-btn text small tabindex="-1" v-bind="attrs" v-on="{ ...menuOn, ...tipOn }">
                      <v-icon small :class="$vuetify.breakpoint.smAndDown ? '' : 'mr-1'">mdi-paperclip</v-icon>
                      <span v-if="!$vuetify.breakpoint.smAndDown">{{
                        $t("components.template.dialog.preview.attachment")
                      }}</span>
                    </v-btn>
                  </template>
                  <span>{{ $t("components.template.dialog.preview.addAttachment") }}</span>
                </v-tooltip>
              </template>
              <v-list dense>
                <v-list-item @click="onPickLocalFile">
                  <v-list-item-title>
                    {{ $t("components.template.dialog.preview.browseThisComputer") }}
                  </v-list-item-title>
                </v-list-item>
                <v-list-item @click="documentSelectDialog = true">
                  <v-list-item-title>
                    {{ $t("components.template.dialog.preview.selectFileFromPlatform") }}
                  </v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
            <v-chip
              v-if="selectedTemplateTitle"
              small
              color="primary"
              outlined
              tabindex="-1"
              class="ml-1"
              @click="openTemplateSelection"
            >
              {{ template.meta.language | getFlagEmojiByLanguage }} {{ selectedTemplateTitle }}
              <v-icon x-small right>mdi-pencil</v-icon>
            </v-chip>
            <template-selection-dialog
              v-if="canReadTemplate"
              ref="templateSelection"
              :hideActivator="!!selectedTemplateTitle"
              :partnerId="partnerId"
              :context="extendedContext"
              :isPlain="isPlain"
              :allowHtml="isEmailInbox"
              @select="onTemplatePicked"
            />
          </template>
        </html-editor>

        <template-editor
          v-else
          :isPlain="isPlain"
          v-model="renderedBody"
          :readOnly="false"
          :partnerId="partnerId"
          :isMobile="isMobile"
          :key="'tpl-' + k"
          :label="$t('components.template.detail.body.body')"
        >
          <span v-if="hasTokens">
            <v-btn
              @click="openCustomTokenDialog"
              text
              small
              :icon="isMobile"
              :class="isMobile ? 'mr-n1 ml-n1' : 'pr-0 pl-0'"
              :color="isTokenWithoutValue ? 'error' : ''"
            >
              <small v-if="!isMobile" class="mr-1 ml-1">
                {{ $t("components.template.dialog.preview.customTokens") }}
              </small>
              <v-icon v-if="!isMobile" :color="isTokenWithoutValue ? 'error' : ''"> mdi-list-status </v-icon>
              <tooltip v-else :text="$t('components.template.dialog.preview.missingTokens')" class="mr-n1 ml-n1">
                <v-icon small :color="isTokenWithoutValue ? 'error' : ''"> mdi-list-status </v-icon>
              </tooltip>
            </v-btn>
            <template-editor-action-divider />
          </span>
          <span v-if="canReadTemplate">
            <template-selection-dialog
              :partnerId="partnerId"
              :context="extendedContext"
              @select="onTemplatePicked"
              :isPlain="isPlain"
              :allowHtml="isEmailInbox"
            />
            <template-editor-action-divider v-if="!isPlain" />
          </span>
        </template-editor>
      </v-col>
      <v-col cols="12" v-if="isHtmlEditorV2Enabled && missingTokenPaths.length" class="pt-0">
        <v-alert dense outlined type="warning" class="mb-0 text-caption">
          {{ $t("components.template.card.missingTokensBanner", { count: missingTokenPaths.length }) }}
          <a class="ml-1 font-weight-medium" @click="openCustomTokenDialog">
            {{ $t("components.template.card.fillTokens") }}
          </a>
        </v-alert>
      </v-col>
    </v-row>

    <v-row class="mb-n5 mt-n6" v-if="problems.length > 0">
      <v-col cols="12">
        <small v-for="problem of problems" :key="problem">{{ problem }}</small>
      </v-col>
    </v-row>

    <template-card-custom-token-dialog ref="tokenDialog" @apply="applyPendingTokens" />

    <v-row dense no-gutters v-if="partnerId && portalContext && canCreateDocument && isAttachmentEnabled">
      <v-col cols="12" class="mt-2">
        <v-row dense v-if="attachments.length > 0">
          <v-col v-for="(a, i) in attachments" :key="a.id || a.name + i" cols="12" sm="6" md="4" lg="3" xl="2">
            <v-card outlined elevation="0" :loading="a.isLoadingInternal">
              <v-row dense align="center" no-gutters>
                <v-col cols="auto" class="mr-2">
                  <v-icon size="32" color="red darken-1">mdi-file-pdf-box</v-icon>
                </v-col>
                <v-col v-if="a.isUploadError">
                  <span class="text-caption grey--text text-truncate d-block">
                    {{ $t("components.template.dialog.preview.uploadError", { fileName: a.name }) }}
                  </span>
                  <v-btn @click="a.create()" x-small elevation="0" color="warning">{{
                    $t("components.template.dialog.preview.reupload")
                  }}</v-btn>
                </v-col>
                <v-col class="text-truncate" v-else>
                  <span class="text-caption grey--text text-truncate d-block">
                    {{ a.name }}
                  </span>
                  <span class="text-caption grey--text d-block">
                    {{ a.size | formatBytes }}
                  </span>
                </v-col>
                <v-col cols="auto">
                  <v-btn icon x-small @click="removeAttachment(i)">
                    <v-icon small>mdi-close</v-icon>
                  </v-btn>
                </v-col>
              </v-row>
            </v-card>
          </v-col>
        </v-row>
      </v-col>
    </v-row>

    <v-alert
      v-if="isAttachmentSizeExceeded"
      type="warning"
      dense
      outlined
      class="mt-2 mb-0"
      data-test="attachment-size-warning"
    >
      {{ $t("components.template.detail.errors.attachmentsTooLarge", attachmentSizeExceededParams) }}
    </v-alert>

    <v-row v-if="sendMode && quotedBody" dense class="mt-1">
      <v-col cols="12">
        <tooltip :text="$t('components.template.dialog.preview.toggleOriginal')">
          <v-btn small text elevation="0" @click="showQuoted = !showQuoted">
            <v-icon small>mdi-dots-horizontal</v-icon>
          </v-btn>
        </tooltip>
        <v-expand-transition>
          <v-card v-show="showQuoted" outlined flat class="mt-2">
            <div v-if="quotedHeader" class="px-3 py-2 text-caption text--secondary">{{ quotedHeader }}</div>
            <v-divider v-if="quotedHeader" />
            <!-- sandbox="" renders the quoted email's HTML/CSS but neutralizes scripts, forms and
                 same-origin access — the body is untrusted HTML from an external sender. -->
            <iframe :srcdoc="quotedBody" sandbox="" class="quoted-mail" title="quoted message"></iframe>
          </v-card>
        </v-expand-transition>
      </v-col>
    </v-row>

    <div v-if="isTicketEnabled && canCreateTicket">
      <ticket-create-checkbox v-model="reminderDate" :enabled.sync="createReminder" />
    </div>
    <debug>
      {{ extendedContext }}
    </debug>
  </v-form>
</template>

<script lang="ts">
import { TemplateTokenConfig } from "@/lib/configuration/template-token.configuration";
import { translateTokenValue } from "@/lib/utility/template-token-catalog";
import { AppContextEnum } from "@/lib/enum/appContext.enum";
import { ConversationChannelEnum } from "@/lib/enum/conversation-channel.enum";
import { LanguageCodeEnum } from "@/lib/enum/language-code.enum";
import { MessageContentFormatEnum } from "@/lib/enum/templateEnums/messageContentFormat.enum";
import { ITemplateContext, ITemplateExtendedContext } from "@/lib/interfaces/template/templateContext.interface";
import { getNestedObjectValues, setNestedObjectValues } from "@/lib/objectPath-helper";
import { emailRule, phoneInternationalRule } from "@/lib/rules/contactRule";
import { requiredRule } from "@/lib/rules/requiredRule";
import { extractTokensFromTemplate, renderTemplate } from "@/lib/utility/renderTemplate";
import PermissionMixin from "@/mixins/PermissionMixin.vue";
import { IPageFilterElement } from "@/models/page-filter-element.entity";
import { IPartnerMessage, PartnerMessage } from "@/models/partner-message.entity";
import { buildEmailMessageDto } from "@/lib/utility/message-compose.helper";
import { IPhoneMessage, PhoneMessage } from "@/models/phone-message.entity";
import { PartnerEntity } from "@/models/partnerEntity";
import { IReference } from "@/models/reference.entity";
import { ISignDocument, SignDocument } from "@/models/sign-document.entity";
import { Ticket } from "@/models/ticket.entity";
import { IUser } from "@/models/user.entity";
import {
  MrfiktivAdminTemplateViewModelGen,
  MrfiktivInboxViewModelGen,
  MrfiktivMessageAttachmentGen,
  MrfiktivPartnerTemplateViewModelGen,
  MrfiktivSettingPartnerInternalViewModelGen,
  MrfiktivSettingViewModelGen
} from "@/services/mrfiktiv/v1/data-contracts";
import { ThgAdminTemplateViewModelGen, ThgPartnerTemplateViewModelGen } from "@/services/thg/v1/data-contracts";
import { BackendResourceEnum, ResourceEnum } from "@/store/enum/authResourceEnum";
import { Config, ConfigModule } from "@/store/modules/config";
import { UserModule } from "@/store/modules/me-user.store";
import { SettingModule } from "@/store/modules/setting.store";
import DocumentSelectionSimpleDialog from "@/views/sign/DocumentSelectionSimpleDialog.vue";
import { debounce } from "debounce";
import { mixins } from "vue-class-component";
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import Card from "../utility/Card.vue";
import ComboBox from "../utility/ComboBox.vue";
import RecipientSelect from "./RecipientSelect.vue";
import ConfirmActionDialog from "../utility/ConfirmActionDialog.vue";
import Debug from "../utility/Debug.vue";
import Tooltip from "../utility/tooltip.vue";
import HtmlEditor from "../utility/HtmlEditor.vue";
import HtmlEditorPlaceholderMenu from "../utility/HtmlEditorPlaceholderMenu.vue";
import TemplateEditor from "./TemplateEditor.vue";
import TemplateEditorActionDivider from "./TemplateEditorActionDivider.vue";
import TemplateSelectionDialog from "./TemplateSelectionDialog.vue";
import TicketCreateCheckbox from "./TicketCreateCheckbox.vue";
import TemplateCardCustomTokenDialog from "./TemplateCardCustomTokenDialog.vue";
import { formatBytes } from "@/lib/utility/file-helper";
import { getFlagEmojiByLanguage } from "@/lib/CountryCodeHelper";
import { $t } from "@/lib/utility/t";
import { PartnerModule } from "@/store/modules/partner";
import { FeatureModule } from "@/store/modules/feature.store";
import { InboxEntityModule } from "@/store/modules/inbox.store";
import { handleError } from "@/lib/utility/handleError";

@Component({
  components: {
    Card,
    ComboBox,
    RecipientSelect,
    Debug,
    ConfirmActionDialog,
    DocumentSelectionSimpleDialog,
    HtmlEditor,
    HtmlEditorPlaceholderMenu,
    TemplateEditor,
    TemplateSelectionDialog,
    TemplateEditorActionDivider,
    Tooltip,
    TicketCreateCheckbox,
    TemplateCardCustomTokenDialog
  },
  filters: { formatBytes, getFlagEmojiByLanguage }
})
export default class TemplateCard extends mixins(PermissionMixin) {
  readonly MessageContentFormatEnum = MessageContentFormatEnum;

  /**
   * Largest raw attachment total the API accepts for one email: it estimates the encoded size as
   * raw * BASE_64_CONVERSION_FACTOR and rejects anything above AWS_SES_MAX_ATTACHMENT_SIZE_MB.
   */
  private readonly MAX_TOTAL_ATTACHMENT_BYTES = Math.floor(
    (Config.AWS_SES_MAX_ATTACHMENT_SIZE_MB * 1024 * 1024) / Config.BASE_64_CONVERSION_FACTOR
  );

  @Prop()
  partnerId?: string;

  @Prop()
  from!: PartnerEntity[];

  @Prop()
  inboxProp?: MrfiktivInboxViewModelGen;

  @Prop()
  channel?: ConversationChannelEnum;

  @Prop({ default: true })
  isTicketEnabled?: boolean;

  @Prop()
  context!: ITemplateContext;

  @Prop()
  loadContext?: () => Promise<ITemplateContext>;

  @Prop()
  to!: string[];

  // If undefined, all "to"s are prefilled
  @Prop()
  prefillTo?: string[];

  /**
   * Phone numbers to offer when the chosen inbox is not an e-mail one.
   *
   * `to` carries recipients of a single kind — the inbox is inferred from it, and a mixed list is
   * inconclusive — so an address and a number cannot share it. A caller that knows both passes the
   * number here, and switching the inbox to SMS or WhatsApp swaps the recipient field over to it.
   * Callers that pass only `to` keep exactly their current behaviour.
   */
  @Prop({ default: () => [] })
  toPhone!: string[];

  @Prop({ default: false })
  isCustomSenderSelection?: boolean;

  @Prop()
  isEditorMobile?: boolean;

  @Prop()
  refs!: IReference[];

  @Prop({ type: Boolean, default: true })
  isAttachmentEnabled!: boolean;

  @Prop({ default: () => [] })
  attachmentFileFilters!: IPageFilterElement[];

  /**
   * Already uploaded documents attached on open (e.g. the invoice PDF when sending an invoice).
   * The user can still remove them or add further attachments via the picker.
   */
  @Prop({ default: () => [] })
  initialAttachments!: ISignDocument[];

  /**
   * Template rendered on open (used by the multi-send dialog to reuse this card per entry).
   * Applied after the initial render so the toolbar shows the template chip and tokens.
   */
  @Prop()
  initialTemplate?: MrfiktivPartnerTemplateViewModelGen | MrfiktivAdminTemplateViewModelGen;

  /** Body prefilled on open; overrides the template-rendered body (restores an edited draft). */
  @Prop()
  prefillBody?: string;

  /** Cc recipients prefilled on open; when non-empty the Cc field is shown. */
  @Prop({ default: () => [] })
  prefillCc!: string[];

  /** Bcc recipients prefilled on open; when non-empty the Bcc field is shown. */
  @Prop({ default: () => [] })
  prefillBcc!: string[];

  /** Extended context (incl. filled custom/setting token values) restored on open (multi-send reuse). */
  @Prop()
  initialExtendedContext?: ITemplateExtendedContext;

  @Prop({ default: () => [] })
  recommendedReceivers!: string[];

  /**
   * When set, send via the reply/forward endpoints (threaded, history appended by backend)
   * instead of creating a fresh message. Requires `replyToMessageId`.
   */
  @Prop()
  sendMode?: "reply" | "forward";

  @Prop()
  replyToMessageId?: string;

  /**
   * Prefills the subject field on open (e.g. the original subject when replying/forwarding).
   */
  @Prop()
  prefillSubject?: string;

  /**
   * HTML body of the original message, shown collapsed below the editor in reply/forward mode.
   */
  @Prop()
  quotedBody?: string;

  /**
   * Header line (sender · date · subject) displayed above the expanded original message.
   */
  @Prop()
  quotedHeader?: string;

  attachmentMenu = false;

  showQuoted = false;

  get isMobile() {
    if (this.isEditorMobile) {
      return true;
    }
    if (this.$vuetify.breakpoint.mdAndDown) {
      return true;
    }
    return false;
  }

  get refKey(): string | undefined {
    if (this.refs && this.refs.length > 0) {
      return this.refs[0].refId;
    }

    return undefined;
  }

  get portalContext() {
    return ConfigModule.appContext === AppContextEnum.REPORT_PORTAL;
  }

  template: ThgAdminTemplateViewModelGen | ThgPartnerTemplateViewModelGen = {
    key: "",
    content: {
      subject: "",
      body: "",
      tokens: []
    },
    meta: {
      description: "",
      categories: [],
      editor: "tiptap",
      contentFormat: "html",
      language: LanguageCodeEnum.DE,
      title: ""
    },
    timestamp: { created: new Date().toString(), lastModified: new Date().toString() },
    isAdminTemplate: false
  };

  reminderDate: string | null = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
  createReminder = false;

  documentSelectDialog = false;

  selectedReceivers: string[] = this.to;
  cc: string[] = [];
  bcc: string[] = [];
  showCc = false;
  showBcc = false;
  selectedSender = this.from?.length > 0 ? this.from[0].companyName : "";

  inbox: MrfiktivInboxViewModelGen | null = this.inboxProp ?? null;

  inboxSenders: MrfiktivInboxViewModelGen[] = [];
  loadingInboxSenders = false;

  renderedBody = "";
  renderedSubject = "";

  loading = false;
  isGenerating = false;
  userHasEditedBody = false;

  isValid = false;
  isReceiverSelectionValid = false;

  problems: string[] = [];

  extendedContext: ITemplateExtendedContext = {};

  attachments: ISignDocument[] = [];
  selected: ISignDocument[] = [];

  /**
   * this key is incremented whenever the template is regenerated.
   * it forces the template editor to rerender to be responsive to changes
   */
  k = 0;

  /**
   * How long to wait for changes before search input is updated
   */
  filterDebounceTimeout = 300;

  get templateTokens() {
    return extractTokensFromTemplate(this.template.content.subject, this.template.content.body);
  }

  get isTokenWithoutValue() {
    for (const token of this.templateTokens) {
      if (!this.getValue(token)) {
        return true;
      }
    }
    return false;
  }

  get hasTokens() {
    return this.templateTokens.length;
  }

  get references() {
    return this.refs || [];
  }

  get templateEditorOffset() {
    if (this.$vuetify.breakpoint.xsOnly) {
      return 190;
    }
    return 150;
  }

  get receiverMailRules() {
    return [emailRule()];
  }

  get phoneNumberRules() {
    return [phoneInternationalRule()];
  }

  get textRules(): any {
    return [requiredRule()];
  }

  get allFormsValid() {
    return this.isValid && this.isReceiverSelectionValid;
  }

  /**
   * Whether every selected receiver passes the channel-appropriate rule (email for an email inbox,
   * phone otherwise). Derived from the data rather than the `v-form` model so it is correct on a
   * prefilled mount — Vuetify's `v-form` validation is lazy and leaves `isValid` false until a field
   * is touched, which is what kept the send button disabled until the receiver was reset (#6039).
   */
  get isReceiverValid(): boolean {
    if (!this.selectedReceivers?.length) {
      return false;
    }
    const rule = this.isEmailInbox ? emailRule() : phoneInternationalRule();
    return this.selectedReceivers.every((receiver) => rule(receiver) === true);
  }

  /**
   * Cc/Bcc are optional, but any entered address must be a valid email. Empty
   * lists are valid (`every` over `[]` is `true`).
   */
  get areOptionalRecipientsValid(): boolean {
    const rule = emailRule();
    return [...this.cc, ...this.bcc].every((recipient) => rule(recipient) === true);
  }

  /**
   * Send-button gate: a valid receiver, a non-empty body, and (for email) a subject. Computed from
   * the underlying values so it does not depend on the lazy `v-form` validation state.
   */
  get isFormValid(): boolean {
    if (!this.isReceiverValid) {
      return false;
    }
    if (!this.renderedBody?.length) {
      return false;
    }
    if (this.isEmailInbox && !this.renderedSubject?.length) {
      return false;
    }
    if (this.isEmailInbox && !this.areOptionalRecipientsValid) {
      return false;
    }
    if (this.isAttachmentSizeExceeded) {
      return false;
    }
    return true;
  }

  /**
   * Whether the attachments are too large to send in one email - including those the caller prefilled
   * via `initialAttachments`, which never pass through `canAddAttachments`.
   */
  get isAttachmentSizeExceeded(): boolean {
    return this.isEmailInbox && this.getTotalAttachmentBytes() > this.MAX_TOTAL_ATTACHMENT_BYTES;
  }

  /**
   * The attachment limit in whole MB, rounded down so it never states more than the API accepts.
   */
  get maxTotalAttachmentMb(): string {
    return Math.floor(this.MAX_TOTAL_ATTACHMENT_BYTES / (1024 * 1024)).toString();
  }

  get attachmentSizeExceededParams() {
    return { size: formatBytes(this.getTotalAttachmentBytes()), maxMb: this.maxTotalAttachmentMb };
  }

  get senderNames() {
    return this.from?.map((s) => s.companyName);
  }

  get selectedSenderId() {
    if (this.inbox) {
      return this.inbox.identifier || this.partnerId || "";
    }

    const sender = this.from?.find((s) => s.companyName === this.selectedSender);

    return sender?._id || "";
  }

  get settingTokens() {
    return this.templateTokens.filter((c: string) => c.split(".")[0] === TemplateTokenConfig.settingCategory);
  }

  get customTokens() {
    return this.templateTokens.filter((c: string) => c.split(".")[0] === TemplateTokenConfig.customCategory);
  }

  get inboxes() {
    const publicInboxes = InboxEntityModule.filteredAndSorted.filter((i) => i.isPublic);
    if (this.channel) {
      return publicInboxes.filter((i) => i.source === this.channel);
    }
    return publicInboxes;
  }

  get hasInboxes(): boolean {
    return this.inboxes.length > 0;
  }

  /** Only let the user change the inbox when there is genuinely more than one to choose from. */
  get canSelectInbox(): boolean {
    return this.inboxes.length > 1;
  }

  /** Localized "default" label for the synthetic catch-all inbox (no real identifier). */
  get defaultInboxLabel(): string {
    const label = this.$t("components.template.dialog.preview.defaultSender");
    return label ? label.toString() : "";
  }

  /**
   * Display label for an inbox. The synthetic catch-all fallback (e.g. name "all") has no real
   * identifier — show the "default" label for it instead of "all ()".
   */
  inboxLabel(inbox: MrfiktivInboxViewModelGen | null): string {
    if (!inbox?.identifier) {
      return this.defaultInboxLabel;
    }
    return `${inbox.name} (${inbox.identifier})`;
  }

  /**
   * Label shown in the read-only sender field when no inbox is selectable. A real selected inbox shows
   * its own label; otherwise prefer the sender name (partner company, passed via `:from`) so the field
   * is informative, falling back to the "no-reply" default only when no sender name is available.
   */
  get defaultSenderLabel(): string {
    if (this.inbox?.identifier) {
      return this.inboxLabel(this.inbox);
    }
    if (this.senderNames?.[0]) {
      return this.senderNames[0];
    }
    return this.defaultInboxLabel;
  }

  /** What the recipient field offers: the numbers on a phone inbox, the addresses otherwise. */
  get recipientOptions(): string[] {
    if (!this.isEmailInbox && this.toPhone?.length) {
      return this.toPhone;
    }

    return this.to ?? [];
  }

  /** What it starts filled with. `prefillTo` still narrows the addresses on an e-mail inbox. */
  get recipientPrefill(): string[] | undefined {
    if (!this.isEmailInbox && this.toPhone?.length) {
      return this.toPhone;
    }

    return this.prefillTo ? this.prefillTo : this.to;
  }

  get isEmailInbox() {
    if (this.channel) {
      return this.channel === ConversationChannelEnum.EMAIL;
    }
    return !this.inbox || this.inbox?.source === "email";
  }

  get isPlain() {
    return this.template.meta.contentFormat === MessageContentFormatEnum.PLAIN || !this.isEmailInbox;
  }

  get isHtmlEditorV2Enabled() {
    return FeatureModule.isHtmlEditorV2Enabled;
  }

  get isReceiverSearchEnabled() {
    return FeatureModule.isReceiverSearchEnabled;
  }

  get selectedTemplateTitle(): string {
    return this.template?.meta?.title || "";
  }

  get missingTokenPaths(): string[] {
    return this.templateTokens.filter((token) => !this.isTokenFilled(token));
  }

  /**
   * Whether a token resolves to a real, user-provided value. Custom tokens are seeded with their own
   * key name in addCustomToContext() (e.g. `custom.name` -> "name"), so a value equal to the key means
   * the user hasn't filled it yet — treat those as unfilled so the missing-placeholder UX triggers.
   */
  private isTokenFilled(token: string): boolean {
    const value = this.getValue(token);
    if (!value) return false;
    const [category, key] = token.split(".");
    if (category === TemplateTokenConfig.customCategory && value === key) return false;
    return true;
  }

  openTemplateSelection() {
    (this.$refs.templateSelection as TemplateSelectionDialog)?.open?.();
  }

  /**
   * Lifecycle hook that prepares the card for sending: resolve the sender inbox, reset the template,
   * build the context, render the initial preview, then validate the form — all behind the loading
   * skeleton so the user never sees an intermediate state.
   *
   * The sender inbox is resolved with a priority cascade:
   * 1. `inboxProp` — only when it is a real inbox (has an `identifier`). Callers such as the inbox view
   *    pass a synthetic "all"/default inbox (empty identifier) that is not part of `inboxes`; honoring
   *    it would leave the v-select bound to a value matching no item (empty). The matching object from
   *    `inboxes` is preferred so the v-select's reference-based selection renders.
   * 2. `channel` — when given, the first inbox whose `source` matches that channel.
   * 3. Inferred from the recipients (`to`): the recipient strings are run through the email and phone
   *    rules purely as classifiers. If every recipient is a valid email, an email inbox is chosen; if
   *    every recipient is a valid phone number, an SMS/WhatsApp inbox is chosen. A mixed or empty list
   *    is inconclusive and matches neither.
   * 4. Fallback to the first available inbox (`inboxes[0]`), or `null` when the partner has none.
   */
  async mounted() {
    this.loading = true;

    if (this.initialAttachments.length) {
      this.attachments = [...this.initialAttachments];
    }

    // Only honor a passed inbox when it is a real one (has an identifier). Callers such as the inbox
    // view pass a synthetic "all"/default inbox (empty identifier) that is not part of `inboxes`, which
    // would leave the v-select with a value matching no item (empty). Prefer the matching object from
    // `inboxes` so the v-select's reference-based selection renders; otherwise fall through to the
    // auto-select below which defaults to the first available inbox.
    const propInbox = this.inboxProp?.identifier
      ? (this.inboxes.find((i) => i.identifier === this.inboxProp?.identifier) ?? this.inboxProp)
      : null;

    if (propInbox) {
      this.inbox = propInbox;
    } else if (this.channel && this.inboxes) {
      this.inbox = this.inboxes.find((i) => i.source === this.channel) ?? null;
    } else {
      // `inbox` may still hold the synthetic prop from its data initializer (`inboxProp ?? null`);
      // clear it so the `inboxes[0]` fallback below is reachable when the recipients are inconclusive.
      this.inbox = null;

      const validateEmail = emailRule();
      const validatePhone = phoneInternationalRule();
      const recipients = (this.to ?? []).filter((v) => typeof v === "string" && v.trim().length > 0);
      const hasOnlyNonEmptyRecipients = recipients.length > 0 && recipients.length === (this.to?.length ?? 0);
      const allEmails = hasOnlyNonEmptyRecipients && recipients.every((v) => validateEmail(v) === true);
      const allPhones = hasOnlyNonEmptyRecipients && recipients.every((v) => validatePhone(v) === true);

      if (allEmails && !allPhones) {
        this.inbox = this.inboxes.find((i) => i.source === ConversationChannelEnum.EMAIL) ?? null;
      } else if (allPhones && !allEmails) {
        this.inbox =
          this.inboxes.find(
            (i) => i.source === ConversationChannelEnum.SMS || i.source === ConversationChannelEnum.WHATSAPP
          ) ?? null;
      }

      if (!this.inbox) {
        this.inbox = this.inboxes[0] ?? null;
      }
    }

    // for some reason this.template was undefined when mounting
    this.template = {
      key: "",
      content: {
        subject: "",
        body: "",
        tokens: []
      },
      meta: {
        description: "",
        categories: [],
        editor: "tiptap",
        contentFormat: "html",
        language: LanguageCodeEnum.DE,
        title: ""
      },
      timestamp: { created: new Date().toString(), lastModified: new Date().toString() },
      isAdminTemplate: false
    };

    await this.setContext();
    this.generateTemplate();

    this.applyPrefillSubject();

    // Multi-send reuse: render the provided template, restore filled token values via the
    // extended context, then restore any prefilled draft (subject already applied above;
    // body/cc/bcc override the template render).
    if (this.initialTemplate) {
      await this.setTemplate(this.initialTemplate);
      this.applyPrefillSubject();
    }
    if (this.initialExtendedContext) {
      this.setExtendedContext(this.initialExtendedContext);
      this.applyPrefillSubject();
    }
    if (this.prefillCc.length) {
      this.cc = [...this.prefillCc];
      this.showCc = true;
    }
    if (this.prefillBcc.length) {
      this.bcc = [...this.prefillBcc];
      this.showBcc = true;
    }
    if (this.prefillBody) {
      this.renderedBody = this.prefillBody;
    }

    this.loading = false;

    this.$nextTick(() => {
      (this.$refs.form as HTMLFormElement)?.validate();
      // Only start emitting drafts after the prefills are fully applied. Emitting during
      // mount would feed intermediate (empty/template) values back to a host bound to the
      // same draft, wiping restored edits.
      this.draftReady = true;
      this.emitDraft();
    });
  }

  /** Guards draft emission until the initial prefills have been applied (multi-send reuse). */
  private draftReady = false;

  /**
   * Emits the current compose draft so a host (e.g. the multi-send dialog) can persist
   * per-entry edits while reusing a single card instance across entries.
   */
  emitDraft() {
    this.$emit("draftChanged", {
      subject: this.renderedSubject,
      body: this.renderedBody,
      to: [...this.selectedReceivers],
      cc: [...this.cc],
      bcc: [...this.bcc],
      attachments: [...this.attachments],
      extendedContext: this.extendedContext,
      missingTokenPaths: [...this.missingTokenPaths]
    });
  }

  @Watch("renderedSubject")
  @Watch("renderedBody")
  @Watch("cc")
  @Watch("bcc")
  @Watch("selectedReceivers")
  @Watch("attachments", { deep: true })
  onDraftFieldChanged() {
    if (this.draftReady) {
      this.emitDraft();
    }
  }

  @Watch("isFormValid", { immediate: true })
  isValidWatcher() {
    this.$emit("isValid", this.isFormValid);
  }

  @Watch("missingTokenPaths")
  onMissingTokensChanged() {
    this.$emit("missingTokens", this.isHtmlEditorV2Enabled && this.missingTokenPaths.length > 0);
  }

  /**
   * Re-apply the prefilled subject when it changes (e.g. the detail view is reused for a
   * different message without remounting this card).
   */
  @Watch("prefillSubject")
  applyPrefillSubject() {
    if (this.prefillSubject) {
      this.renderedSubject = this.prefillSubject;
    }
  }

  @Watch("inbox")
  onInboxChanged() {
    this.$nextTick(() => {
      (this.$refs.form as HTMLFormElement)?.validate();
    });
  }

  @Watch("renderedBody")
  onRenderedBodyChanged() {
    if (!this.isGenerating) {
      this.userHasEditedBody = true;
    }
  }

  getLabel(tokenPath: string) {
    const translationPrefix = "components.template.editor.placeholder.path";
    const translation = this.$t(`${translationPrefix}.${tokenPath}`);
    if (typeof translation !== "string" || translation.startsWith(translationPrefix)) {
      return tokenPath;
    }
    return translation;
  }

  getValue(tokenPath: string) {
    return getNestedObjectValues(this.extendedContext, tokenPath);
  }

  setValue(tokenPath: string, value: string) {
    return setNestedObjectValues(this.extendedContext, tokenPath, value);
  }

  openCustomTokenDialog() {
    const tokens = extractTokensFromTemplate(this.template.content.subject, this.template.content.body);
    (this.$refs.tokenDialog as TemplateCardCustomTokenDialog).open(
      tokens,
      this.extendedContext,
      this.userHasEditedBody
    );
  }

  resolveTokenValue(token: string): string {
    // Return empty for unfilled tokens (incl. custom tokens still seeded with their key) so the editor
    // inserts a placeholder chip and emits `placeholder-missing` instead of inserting the key text.
    if (!this.isTokenFilled(token)) return "";
    const rendered = renderTemplate(`{{${token}}}`, this.extendedContext);
    return translateTokenValue(token, rendered);
  }

  /** Display-only value for the token picker/menu; translates known enum-backed tokens. */
  resolveTokenDisplayValue(token: string): string {
    const value = String(this.getValue(token) ?? "");
    return translateTokenValue(token, value);
  }

  /** Inserts a resolved token value into the subject field at the current caret position. */
  insertSubjectPlaceholder(token: string) {
    const value = this.resolveTokenDisplayValue(token);
    const field = (this.$refs.subjectField as (Vue & { $refs: { input: HTMLInputElement } }) | undefined)?.$refs?.input;
    const start = field?.selectionStart ?? this.renderedSubject.length;
    const end = field?.selectionEnd ?? this.renderedSubject.length;

    this.renderedSubject = this.renderedSubject.slice(0, start) + value + this.renderedSubject.slice(end);

    this.$nextTick(() => {
      const caret = start + value.length;
      field?.setSelectionRange(caret, caret);
      field?.focus();
    });
  }

  onTokenClick(token: string) {
    (this.$refs.tokenDialog as TemplateCardCustomTokenDialog).open(
      [token],
      this.extendedContext,
      this.userHasEditedBody
    );
  }

  applyPendingTokens(values: Record<string, string>, regenerate: boolean) {
    for (const [tokenPath, value] of Object.entries(values)) {
      this.setValue(tokenPath, value);
    }
    this.extendedContext = { ...this.extendedContext };

    if (regenerate) {
      this.generateTemplate();
    }

    // Capture the filled token values in the draft even when the body was not regenerated,
    // so a multi-send host persists them across entry switches.
    if (this.draftReady) {
      this.emitDraft();
    }
  }

  /**
   * Sets the template to loading
   *
   * @param loading
   */
  setLoading(loading: boolean) {
    this.loading = loading;
  }

  changeReceiverSelection(selectedReceivers: string[]) {
    this.selectedReceivers = selectedReceivers;
  }

  changeIsReceiverSelectionValid(isValid: boolean) {
    this.isReceiverSelectionValid = isValid;
  }

  changeCcSelection(cc: string[]) {
    this.cc = cc;
  }

  changeBccSelection(bcc: string[]) {
    this.bcc = bcc;
  }

  debounceInput = debounce(
    (v: any, customTokenKey: string) => this.handleCustomContextChange(v, customTokenKey),
    this.filterDebounceTimeout,
    false
  );

  get my(): IUser {
    return UserModule.user;
  }

  get myUserId() {
    return UserModule.userId;
  }

  async setContext() {
    const context = (await this.getContext()) || {};
    this.extendedContext = { ...context, custom: {}, my: this.my };
  }

  async getContext() {
    const context = this.context;
    if (context) {
      return context;
    }

    const loadContext = this.loadContext;
    if (loadContext) {
      try {
        const context = await loadContext();

        return context;
      } catch (e) {
        handleError(e);
      }
    }
    this.$log.error("either context or load context must be given");
  }

  /**
   * Add a key value to the extended context manually from outside
   * @param key
   * @param value
   */
  extendCustomExtendedContext(key: string, value: string) {
    // this.extendedContext = { ...this.extendedContext, custom: { ...this.extendedContext.custom, [key]: value } };
    this.debounceInput(value, key);
  }

  async buildExtendedContext() {
    this.$log.debug("EXTENDED CONTEXT CHANGED");
    this.addCustomToContext();
    this.$log.debug("ADDED CUSTOM TOKENS");
    await this.addSettingsToContext();
    this.$log.debug("ADDED SETTINGS TO CONTEXT");
  }

  async loadSetting(key: string) {
    let setting: MrfiktivSettingViewModelGen | MrfiktivSettingPartnerInternalViewModelGen | undefined;

    if (this.partnerId) {
      setting = await SettingModule.getSettingByKey({ key: key, partnerId: this.partnerId });

      if (!setting) {
        this.problems.push(`Setting ${key} not found`);
      }
      if (setting?.isEncrypted) {
        this.problems.push(`Setting ${key} encrypted`);
      }
    } else {
      try {
        setting = await SettingModule.getAdminSettingByKey(key);
        if (setting?.isEncrypted) {
          this.problems.push(`Setting ${key} encrypted`);
        }
      } catch (e) {
        this.problems.push(`Setting ${key} not found`);
      }
    }

    return setting?.value;
  }

  handleCustomContextChange(v: any, customTokenKey: string) {
    this.setValue(customTokenKey, v);
    this.extendedContext = { ...this.extendedContext };

    this.generateTemplate();
  }

  addCustomToContext() {
    const settingContext: Record<string, string> = {};

    this.customTokens.forEach((t) => {
      const key = t.split(".")[1];
      settingContext[key] = `${key}`;
    });

    this.extendedContext.custom = settingContext;
  }

  async addSettingsToContext() {
    const settingsAsync: Promise<{ key: string; value: string | undefined }>[] = this.settingTokens
      .map((st) => st.split(".")[1])
      .map(async (key) => {
        return {
          key: key,
          value: await this.loadSetting(key)
        };
      });

    const settings = await Promise.all(settingsAsync);

    const settingContext: Record<string, string> = {};
    settings.forEach((s) => (settingContext[s.key] = s.value || ""));

    this.extendedContext.setting = settingContext;
  }

  generateTemplate() {
    this.isGenerating = true;
    const input = this.extendedContext;

    // In reply/forward mode the subject is the threaded "Re:/Fwd:" subject — prefilled (see
    // applyPrefillSubject) and editable. Selecting a template must not overwrite it with the
    // template's own subject, so only derive the subject from the template on the normal compose path.
    if (!this.sendMode) {
      this.renderedSubject = renderTemplate(this.template.content.subject, input);
    }
    this.renderedBody = renderTemplate(this.template.content.body, input);

    this.k++;
    this.$nextTick(() => {
      this.isGenerating = false;
      this.userHasEditedBody = false;
    });
    this.$emit("missingTokens", this.isHtmlEditorV2Enabled && this.missingTokenPaths.length > 0);
  }

  setExtendedContext(extendedContext: ITemplateExtendedContext) {
    this.extendedContext = extendedContext;
    this.generateTemplate();
  }

  async setTemplate(template: MrfiktivPartnerTemplateViewModelGen | MrfiktivAdminTemplateViewModelGen) {
    this.template = template;
    await this.buildExtendedContext();
    this.generateTemplate();
  }

  /**
   * Template picked by the user in the toolbar. Applies it and notifies the host so a
   * multi-send dialog can propagate the choice to the other entries.
   */
  async onTemplatePicked(template: MrfiktivPartnerTemplateViewModelGen | MrfiktivAdminTemplateViewModelGen) {
    await this.setTemplate(template);
    this.$emit("templateSelected", template);
  }

  assertMailInformation() {
    const errors: string[] = [];
    const missingErrors: string[] = [];
    if (!this.selectedSenderId) {
      missingErrors.push(this.$t("components.template.detail.errors.sender").toString());
    }
    if (!this.selectedReceivers?.length) {
      missingErrors.push(this.$t("components.template.detail.errors.receiver").toString());
    }
    if (!this.renderedSubject) {
      missingErrors.push(this.$t("components.template.detail.errors.subject").toString());
    }
    if (!this.renderedBody) {
      missingErrors.push(this.$t("components.template.detail.errors.body").toString());
    }
    if (this.attachments && this.attachments.length && this.attachments.some((a) => a.isLoadingInternal)) {
      missingErrors.push(this.$t("components.template.detail.errors.attachment").toString());
    }
    if (missingErrors.length) {
      errors.push(missingErrors.join(", ") + " " + this.$t("components.template.detail.errors.missing").toString());
    }
    if (this.isAttachmentSizeExceeded) {
      errors.push(
        this.$t("components.template.detail.errors.attachmentsTooLarge", this.attachmentSizeExceededParams).toString()
      );
    }

    // In reply mode the backend resolves the recipient from the original thread, so the
    // prefilled recipient (which may be in "Name <mail>" format) is not validated here.
    if (this.sendMode !== "reply" && this.selectedReceivers.length)
      for (const receiver of this.selectedReceivers) {
        const rule = emailRule()(receiver);
        if (rule && rule !== true) {
          errors.push(rule);
        }
      }

    for (const recipient of [...this.cc, ...this.bcc]) {
      const rule = emailRule()(recipient);
      if (rule && rule !== true) {
        errors.push(rule);
      }
    }

    if (errors.length) {
      throw new Error(errors.join(", "));
    }
  }

  assertSmsInformation() {
    const errors: string[] = [];
    const missingErrors: string[] = [];

    if (!this.selectedReceivers?.length) {
      missingErrors.push(this.$t("components.template.detail.errors.receiver").toString());
    }
    if (!this.renderedBody) {
      missingErrors.push(this.$t("components.template.detail.errors.body").toString());
    }

    if (missingErrors.length) {
      errors.push(missingErrors.join(", ") + " " + this.$t("components.template.detail.errors.missing").toString());
    }

    // Validate phone numbers (E.164 format with optional whitespaces)
    if (this.selectedReceivers.length) {
      for (const receiver of this.selectedReceivers) {
        const phoneRegex = /^\+[1-9][\d\s]{1,14}$/;
        if (!phoneRegex.test(receiver)) {
          errors.push(this.$t("components.template.detail.errors.invalidPhoneNumber", { number: receiver }).toString());
        }
      }
    }

    if (errors.length) {
      throw new Error(errors.join(", "));
    }
  }

  async send() {
    if (this.isEmailInbox) {
      return await this.sendMail();
    }

    return await this.sendSms();
  }

  validate() {
    return (this.$refs.form as HTMLFormElement).validate();
  }

  async sendSms() {
    try {
      this.assertSmsInformation();
      this.loading = true;

      const phoneMessages: IPhoneMessage[] = [];

      for (const receiver of this.selectedReceivers) {
        const phoneMessage = PhoneMessage.fromSmsDto(
          PartnerModule.partner.id,
          {
            identifier: this.inbox?.identifier || "",
            to: receiver,
            body: this.renderedBody
          },
          this.refs
        );

        const sentMessage = await phoneMessage.sendSms();
        phoneMessages.push(sentMessage);
      }

      if (this.isTicketEnabled && this.canCreateTicket && this.createReminder) {
        const ticketRefs = this.refs ? [...this.refs] : [];
        for (const phoneMessage of phoneMessages) {
          ticketRefs.push({ refId: phoneMessage.id, refType: BackendResourceEnum.MESSAGE });
        }

        const ticket = new Ticket({
          partnerId: PartnerModule.partner.id,
          title: this.renderedBody.slice(0, 100), // Use first 100 chars of body as title
          refs: ticketRefs,
          assignees: [this.myUserId]
        });

        ticket.due = this.reminderDate ? new Date(this.reminderDate).toISOString() : undefined;

        await ticket.create();
      }

      this.$toast.success(this.$t("components.template.dialog.preview.smsSentConfirmation"));

      // Clear the form
      this.template.content.body = "";
      this.generateTemplate();

      this.$emit("sent", phoneMessages);

      return phoneMessages;
    } catch (e) {
      handleError(e);
    } finally {
      this.loading = false;
    }
  }

  async sendMail() {
    try {
      if (this.sendMode && !this.replyToMessageId) {
        throw new Error("replyToMessageId is required when sendMode is set");
      }

      this.assertMailInformation();
      this.loading = true;

      const messages = await this.buildAndSendMessages();

      if (this.isTicketEnabled && this.canCreateTicket && this.createReminder) {
        const ticketRefs = this.refs ? [...this.refs] : [];
        for (const message of messages) {
          ticketRefs.push({ refId: message.id, refType: ResourceEnum.MESSAGE });
        }

        const ticket = new Ticket({
          partnerId: this.partnerId,
          title: this.renderedSubject,
          refs: ticketRefs,
          assignees: [this.myUserId]
        });

        ticket.due = this.reminderDate ? new Date(this.reminderDate).toISOString() : undefined;

        await ticket.create();
      }

      this.$toast.success(this.$t("components.template.dialog.preview.sentConfirmation"));

      this.template.content.subject = "";
      this.template.content.body = "";
      this.attachments = [];
      this.cc = [];
      this.bcc = [];
      this.showCc = false;
      this.showBcc = false;
      this.generateTemplate();

      this.$emit("sent", messages);

      return messages;
    } catch (e) {
      handleError(e);
    } finally {
      this.loading = false;
    }
  }

  /**
   * Builds the outgoing PartnerMessage from the composer state and sends it via the path for the
   * current mode. buildEmailMessageDto owns the recipient ";"-join, and the message carries all the
   * data, so reply()/forward()/create() take no arguments. Reply/forward thread the original message
   * (its id is passed as `id`); otherwise a fresh message is created.
   */
  private async buildAndSendMessages(): Promise<IPartnerMessage[]> {
    const attachments: MrfiktivMessageAttachmentGen[] | undefined = this.attachments.length
      ? this.attachments.map((a) => ({
          name: a.name,
          type: "application/pdf",
          size: a.size || 0,
          ref: {
            refId: a.id || "",
            refType: ResourceEnum.DOCUMENT
          }
        }))
      : undefined;

    const message = new PartnerMessage({
      partnerId: this.partnerId,
      id: this.replyToMessageId,
      ...buildEmailMessageDto({
        to: this.selectedReceivers,
        cc: this.cc,
        bcc: this.bcc,
        subject: this.renderedSubject,
        body: this.renderedBody,
        attachments
      }),
      refs: this.refs ?? []
    });

    if (this.sendMode === "reply") {
      return [await message.reply()];
    }
    if (this.sendMode === "forward") {
      return [await message.forward()];
    }
    return [await message.create()];
  }

  onPickLocalFile() {
    this.attachmentMenu = false;
    (this.$refs.localFileInput as HTMLInputElement).click();
  }

  async onLocalFilesSelected(e: Event) {
    const input = e.target as HTMLInputElement;
    if (!input.files) return;

    const files = Array.from(input.files);
    await this.addAttachmentFiles(files);

    input.value = "";
  }

  async addAttachmentFiles(files: File[]) {
    const newTotal = files.reduce((sum, f) => sum + f.size, 0);

    if (!this.canAddAttachments(newTotal)) {
      return;
    }

    const newItems = files.map(
      (f) =>
        new SignDocument({
          name: f.name,
          title: f.name,
          isTemplate: false,
          folder: "/attachments/",
          refs: this.refs,
          file: f,
          partnerId: this.partnerId
        })
    );
    this.attachments.push(...newItems);

    await Promise.all(
      newItems.map(async (item) => {
        try {
          await item.create();
        } catch (error) {
          this.$log.error("Attachment upload failed", error);
          this.$toast?.error?.(this.$t("components.template.dialog.preview.attachmentUploadFailed").toString());
          const index = this.attachments.indexOf(item);
          if (index !== -1) this.attachments.splice(index, 1);
        }
      })
    );
  }

  async onEditorFilesDropped(files: File[]) {
    if (
      !(this.partnerId && this.portalContext && this.canCreateDocument && this.isAttachmentEnabled && this.isEmailInbox)
    )
      return;
    const pdfs = files.filter((f) => f.type === "application/pdf" || f.name.toLowerCase().endsWith(".pdf"));
    if (!pdfs.length) {
      this.$toast?.info?.(this.$t("components.template.dialog.preview.onlyPdfAttachments").toString());
      return;
    }
    await this.addAttachmentFiles(pdfs);
  }

  getTotalAttachmentBytes() {
    return this.attachments.reduce((sum, a) => sum + (a.size || 0), 0);
  }

  removeAttachment(i: number) {
    this.attachments.splice(i, 1);
  }

  /**
   * Check if new attachments can be added without exceeding max total size
   * @param newBytes
   */
  canAddAttachments(newBytes: number): boolean {
    const currentTotal = this.getTotalAttachmentBytes();

    if (currentTotal + newBytes > this.MAX_TOTAL_ATTACHMENT_BYTES) {
      this.$toast.error(
        $t("components.template.detail.errors.toLarge", { maxMb: this.maxTotalAttachmentMb }).toString()
      );
      return false;
    }

    return true;
  }

  attachDocuments(documents: ISignDocument[]) {
    const invalidDocs = documents.filter((d) => !d.id);
    if (invalidDocs.length > 0) {
      this.$toast.error($t("components.template.detail.errors.missingId").toString());
      return;
    }

    const existingIds = new Set(this.attachments.map((a) => a.id).filter((id): id is string => !!id));

    const uniqueNewDocs: ISignDocument[] = [];
    let droppedCount = 0;

    for (const d of documents) {
      if (!d.id) {
        this.$toast.error($t("components.template.detail.errors.missingId").toString());
        return;
      }
      if (existingIds.has(d.id)) {
        droppedCount++;
        continue;
      }
      uniqueNewDocs.push(d);
    }

    if (droppedCount > 0) {
      this.$toast.info($t("components.template.detail.errors.duplicate", { droppedCount }).toString());
    }

    const newTotal = uniqueNewDocs.reduce((sum, d) => sum + (d.size || 0), 0);

    if (!this.canAddAttachments(newTotal)) {
      return;
    }

    this.attachments.push(...uniqueNewDocs);
    this.selected = [];
  }

  selectInboxSender(inbox: MrfiktivInboxViewModelGen) {
    if (inbox) {
      this.inbox = inbox;
      this.selectedSender = ""; // Clear regular sender selection
      this.$emit("senderChanged", inbox);
    }
  }

  clearInboxSender() {
    this.inbox = null;
    if (this.from?.length > 0) {
      this.selectedSender = this.from[0].companyName;
    }
    this.$emit("senderChanged", null);
  }
}
</script>

<style scoped>
.preview {
  width: 100%;
  border-radius: 5px;
  border-style: solid;
  border-width: 1px;
  border-color: lightgrey;
  height: calc(100% - 52px);
}

.quoted-mail {
  width: 100%;
  height: 240px;
  border: none;
}
</style>
